import { ExportButtonGraphql } from "components/actions/ExportButtonGraphql";
import ExportEntityPaywallButton from "components/actions/ExportEntityPaywallButton";
import { toast } from "components/ui/toast/Toast";
import { useNoticeTopicExport } from "lib/hooks/api/notices/useNoticeTopicExport";
import { useSubscription } from "lib/hooks/subscription";
import { EventDataTypes } from "lib/tracking";
import { DEFAULT_NOTICE_SEMANTIC_SORT, type NoticeSemanticSort } from "./noticeSemanticSort";
import { convertNoticeV2FiltersToSearchRequest, NoticeV2Filters } from "./utils";

export function ExportNoticesV2Button({
  filters,
  semanticSort = DEFAULT_NOTICE_SEMANTIC_SORT,
  selectedNotices,
  extraDropdownItems,
  enabled,
  totalResults,
  isLoadingTotalResults,
}: {
  filters: NoticeV2Filters;
  semanticSort?: NoticeSemanticSort;
  selectedNotices?: string[];
  extraDropdownItems?: React.ReactNode;
  enabled?: boolean;
  totalResults: number;
  isLoadingTotalResults: boolean;
}) {
  // If data sources include BUYERS|SUPPLIERS, then this user is on either the Growth or Expert plan and is authorised to export notices.
  const subscription = useSubscription();
  const authorised = subscription.hasDataTypes("SUPPLIERS") && subscription.hasDataTypes("BUYERS");

  if (!authorised) {
    return <ExportEntityPaywallButton />;
  }

  return (
    <ExportNoticesV2ButtonInternal
      filters={filters}
      semanticSort={semanticSort}
      selectedNotices={selectedNotices}
      extraDropdownItems={extraDropdownItems}
      enabled={enabled}
      totalResults={totalResults}
      isLoadingTotalResults={isLoadingTotalResults}
    />
  );
}

function ExportNoticesV2ButtonInternal({
  filters,
  semanticSort,
  selectedNotices,
  extraDropdownItems,
  enabled,
  totalResults: externalTotalResults,
  isLoadingTotalResults,
}: {
  filters: NoticeV2Filters;
  semanticSort: NoticeSemanticSort;
  selectedNotices?: string[];
  extraDropdownItems?: React.ReactNode;
  enabled?: boolean;
  totalResults: number;
  isLoadingTotalResults: boolean;
}) {
  const noticeExportMutation = useNoticeTopicExport({
    onError: () => toast.error("Failed to start export. Please try again later."),
  });

  const totalResults =
    selectedNotices && selectedNotices.length > 0 ? selectedNotices.length : externalTotalResults;

  const noticeSearchRequest = convertNoticeV2FiltersToSearchRequest(
    filters,
    {
      current: 1,
      pageSize: totalResults + 1,
    },
    semanticSort,
  );

  return (
    <ExportButtonGraphql
      isLoadingTotalResults={isLoadingTotalResults}
      isEnabledTotalResults={enabled}
      totalResults={totalResults}
      exportMutation={noticeExportMutation}
      exportVariables={{
        input: noticeSearchRequest,
        selectedNotices: { noticeIds: selectedNotices },
      }}
      exportDataType={EventDataTypes.record}
      extraDropdownItems={extraDropdownItems}
    />
  );
}
